import React, { useState, useEffect, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import { getApiBase, getAdminEmail } from "../api";

export default function WellsiteManager() {
  const apiBase = useMemo(() => getApiBase(), []);
  const adminEmail = useMemo(() => getAdminEmail(), []);
  const [wellsites, setWellsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWellsite, setSelectedWellsite] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filters, setFilters] = useState({ state: "", h2s: "", search: "" });
  const [shippers, setShippers] = useState([]);

  const [formData, setFormData] = useState({
    shipperCompanyId: "",
    name: "",
    wellApiNumber: "",
    fieldName: "",
    latitude: "",
    longitude: "",
    directions: "",
    nearestTown: "",
    county: "",
    state: "TX",
    siteContactName: "",
    siteContactPhone: "",
    h2sPresent: false,
    h2sConcentration: "",
    specialPPE: ["FRC", "Steel Toe Boots", "Hard Hat"],
    operatingHours: "24/7"
  });

  useEffect(() => {
    fetchWellsites();
    fetchShippers();
  }, [filters]);

  async function fetchWellsites() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.state) params.append("state", filters.state);
      if (filters.h2s) params.append("h2s", filters.h2s);
      if (filters.search) params.append("search", filters.search);

      const res = await fetch(`${apiBase}/oilfield/wellsites?${params}`, {
        headers: { "x-admin-email": adminEmail }
      });
      if (res.ok) {
        const data = await res.json();
        setWellsites(data.wellsites || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchShippers() {
    const res = await fetch(`${apiBase}/broker/shippers`, {
      headers: { "x-admin-email": adminEmail }
    });
    if (res.ok) {
      const data = await res.json();
      setShippers(data.shippers || []);
    }
  }

  async function createWellsite(e) {
    e.preventDefault();
    try {
      const res = await fetch(`${apiBase}/oilfield/wellsites`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-email": adminEmail },
        body: JSON.stringify({
          ...formData,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude)
        })
      });
      if (res.ok) {
        setShowCreateForm(false);
        fetchWellsites();
        setFormData({
          shipperCompanyId: "", name: "", wellApiNumber: "", fieldName: "",
          latitude: "", longitude: "", directions: "", nearestTown: "", county: "", state: "TX",
          siteContactName: "", siteContactPhone: "", h2sPresent: false, h2sConcentration: "",
          specialPPE: ["FRC", "Steel Toe Boots", "Hard Hat"], operatingHours: "24/7"
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  const fieldNameGroups = useMemo(() => {
    const groups = {};
    wellsites.forEach(w => {
      const field = w.fieldName || "Other";
      if (!groups[field]) groups[field] = [];
      groups[field].push(w);
    });
    return groups;
  }, [wellsites]);

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>
      <PageHeader 
        title="📍 Wellsite Manager" 
        subtitle="Manage oilfield locations with GPS coordinates, directions, and safety requirements"
      />

      {/* Filters & Actions */}
      <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 16, marginBottom: 20, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          placeholder="🔍 Search wellsites..."
          value={filters.search}
          onChange={e => setFilters({ ...filters, search: e.target.value })}
          style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db", minWidth: 200 }}
        />

        <select
          value={filters.state}
          onChange={e => setFilters({ ...filters, state: e.target.value })}
          style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
        >
          <option value="">All States</option>
          <option value="TX">Texas</option>
          <option value="NM">New Mexico</option>
          <option value="OK">Oklahoma</option>
          <option value="ND">North Dakota</option>
          <option value="CO">Colorado</option>
        </select>

        <select
          value={filters.h2s}
          onChange={e => setFilters({ ...filters, h2s: e.target.value })}
          style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
        >
          <option value="">All H2S Status</option>
          <option value="true">H2S Present ☠️</option>
          <option value="false">No H2S</option>
        </select>

        <button
          onClick={() => setShowCreateForm(true)}
          style={{ marginLeft: "auto", padding: "10px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}
        >
          + Add Wellsite
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#2563eb" }}>{wellsites.length}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Total Wellsites</div>
        </div>
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#059669" }}>{Object.keys(fieldNameGroups).length}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Fields</div>
        </div>
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#dc2626" }}>{wellsites.filter(w => w.h2sPresent).length}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>H2S Sites</div>
        </div>
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#7c3aed" }}>{wellsites.filter(w => w.rigOnSite).length}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Active Rigs</div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: "grid", gridTemplateColumns: "350px 1fr", gap: 20 }}>
        {/* Wellsite List */}
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb", fontWeight: 700 }}>
            Wellsites ({wellsites.length})
          </div>
          <div style={{ maxHeight: 600, overflowY: "auto" }}>
            {Object.entries(fieldNameGroups).map(([fieldName, sites]) => (
              <div key={fieldName}>
                <div style={{ padding: "8px 16px", background: "#f9fafb", fontSize: 12, fontWeight: 600, color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
                  📍 {fieldName} ({sites.length})
                </div>
                {sites.map(site => (
                  <div
                    key={site.id}
                    onClick={() => setSelectedWellsite(site)}
                    style={{
                      padding: 12,
                      borderBottom: "1px solid #f3f4f6",
                      cursor: "pointer",
                      background: selectedWellsite?.id === site.id ? "#eff6ff" : "white"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {site.h2sPresent && <span title="H2S Present">☠️</span>}
                      {site.rigOnSite && <span title="Rig on Site">🏗️</span>}
                      <span style={{ fontWeight: 500, fontSize: 14 }}>{site.name}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                      {site.nearestTown}, {site.state} • {site.shipperName}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Wellsite Details */}
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb" }}>
          {selectedWellsite ? (
            <div style={{ padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 20 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{selectedWellsite.name}</h2>
                  <div style={{ color: "#6b7280", marginTop: 4 }}>
                    {selectedWellsite.fieldName} • {selectedWellsite.shipperName}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {selectedWellsite.h2sPresent && (
                    <span style={{ padding: "4px 12px", background: "#fee2e2", color: "#991b1b", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      ☠️ H2S PRESENT
                    </span>
                  )}
                  {selectedWellsite.operatingHours === "24/7" && (
                    <span style={{ padding: "4px 12px", background: "#d1fae5", color: "#065f46", borderRadius: 20, fontSize: 12 }}>
                      24/7 Operations
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                {/* Location Info */}
                <div>
                  <h4 style={{ margin: "0 0 12px 0", color: "#374151" }}>📍 Location</h4>
                  <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
                    <p style={{ margin: "0 0 8px 0", fontSize: 14 }}>
                      <strong>Coordinates:</strong> {selectedWellsite.latitude?.toFixed(6)}, {selectedWellsite.longitude?.toFixed(6)}
                    </p>
                    <p style={{ margin: "0 0 8px 0", fontSize: 14 }}>
                      <strong>Nearest Town:</strong> {selectedWellsite.nearestTown}, {selectedWellsite.state}
                    </p>
                    <p style={{ margin: "0 0 8px 0", fontSize: 14 }}>
                      <strong>County:</strong> {selectedWellsite.county || "—"}
                    </p>
                    {selectedWellsite.wellApiNumber && (
                      <p style={{ margin: 0, fontSize: 14 }}>
                        <strong>API #:</strong> {selectedWellsite.wellApiNumber}
                      </p>
                    )}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-block", marginTop: 12, color: "#2563eb", fontSize: 13 }}
                    >
                      Open in Google Maps →
                    </a>
                  </div>
                </div>

                {/* Directions */}
                <div>
                  <h4 style={{ margin: "0 0 12px 0", color: "#374151" }}>🚗 Directions</h4>
                  <div style={{ background: "#fef3c7", borderRadius: 8, padding: 16, fontSize: 14, lineHeight: 1.6 }}>
                    {selectedWellsite.directions || "No specific directions provided. Use GPS coordinates."}
                  </div>
                  {selectedWellsite.gateCode && (
                    <div style={{ marginTop: 12, padding: 12, background: "#dbeafe", borderRadius: 8 }}>
                      <strong>Gate Code:</strong> {selectedWellsite.gateCode}
                    </div>
                  )}
                </div>

                {/* Safety */}
                <div>
                  <h4 style={{ margin: "0 0 12px 0", color: "#374151" }}>⚠️ Safety Requirements</h4>
                  <div style={{ background: selectedWellsite.h2sPresent ? "#fee2e2" : "#f0fdf4", borderRadius: 8, padding: 16 }}>
                    {selectedWellsite.h2sPresent && (
                      <div style={{ marginBottom: 12, padding: 8, background: "#991b1b", color: "white", borderRadius: 6, fontWeight: 600 }}>
                        ☠️ H2S PRESENT {selectedWellsite.h2sConcentration && `- ${selectedWellsite.h2sConcentration}`}
                      </div>
                    )}
                    <p style={{ margin: "0 0 8px 0", fontSize: 14 }}><strong>Required PPE:</strong></p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {(selectedWellsite.specialPPE || []).map((ppe, i) => (
                        <span key={i} style={{ padding: "4px 8px", background: "white", borderRadius: 4, fontSize: 12 }}>
                          {ppe}
                        </span>
                      ))}
                    </div>
                    {selectedWellsite.safetyOrientation && (
                      <p style={{ margin: "12px 0 0 0", fontSize: 13, color: "#6b7280" }}>
                        {selectedWellsite.safetyOrientation}
                      </p>
                    )}
                  </div>
                </div>

                {/* Contact */}
                <div>
                  <h4 style={{ margin: "0 0 12px 0", color: "#374151" }}>📞 Site Contact</h4>
                  <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
                    {selectedWellsite.siteContactName ? (
                      <>
                        <p style={{ margin: "0 0 8px 0", fontSize: 14 }}>
                          <strong>Name:</strong> {selectedWellsite.siteContactName}
                        </p>
                        <p style={{ margin: 0, fontSize: 14 }}>
                          <strong>Phone:</strong>{" "}
                          <a href={`tel:${selectedWellsite.siteContactPhone}`} style={{ color: "#2563eb" }}>
                            {selectedWellsite.siteContactPhone}
                          </a>
                        </p>
                      </>
                    ) : (
                      <p style={{ margin: 0, color: "#6b7280" }}>No contact information available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: 60, textAlign: "center", color: "#6b7280" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📍</div>
              <p>Select a wellsite to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: 12, padding: 24, width: 600, maxHeight: "90vh", overflow: "auto" }}>
            <h2 style={{ margin: "0 0 20px 0" }}>Add New Wellsite</h2>
            <form onSubmit={createWellsite}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Shipper *</label>
                  <select
                    value={formData.shipperCompanyId}
                    onChange={e => setFormData({ ...formData, shipperCompanyId: e.target.value })}
                    required
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  >
                    <option value="">Select shipper...</option>
                    {shippers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Site Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Permian Basin Well #47"
                    required
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Field Name</label>
                  <input
                    type="text"
                    value={formData.fieldName}
                    onChange={e => setFormData({ ...formData, fieldName: e.target.value })}
                    placeholder="e.g., Permian Basin"
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>API Well Number</label>
                  <input
                    type="text"
                    value={formData.wellApiNumber}
                    onChange={e => setFormData({ ...formData, wellApiNumber: e.target.value })}
                    placeholder="e.g., 42-371-12345"
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Latitude *</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={e => setFormData({ ...formData, latitude: e.target.value })}
                    placeholder="31.5000"
                    required
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Longitude *</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={e => setFormData({ ...formData, longitude: e.target.value })}
                    placeholder="-102.5000"
                    required
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>State *</label>
                  <select
                    value={formData.state}
                    onChange={e => setFormData({ ...formData, state: e.target.value })}
                    required
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  >
                    <option value="TX">Texas</option>
                    <option value="NM">New Mexico</option>
                    <option value="OK">Oklahoma</option>
                    <option value="ND">North Dakota</option>
                    <option value="CO">Colorado</option>
                    <option value="WY">Wyoming</option>
                    <option value="LA">Louisiana</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Nearest Town</label>
                  <input
                    type="text"
                    value={formData.nearestTown}
                    onChange={e => setFormData({ ...formData, nearestTown: e.target.value })}
                    placeholder="e.g., Midland"
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Directions (Important!)</label>
                  <textarea
                    value={formData.directions}
                    onChange={e => setFormData({ ...formData, directions: e.target.value })}
                    placeholder="Turn-by-turn directions from nearest highway..."
                    rows={3}
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Site Contact Name</label>
                  <input
                    type="text"
                    value={formData.siteContactName}
                    onChange={e => setFormData({ ...formData, siteContactName: e.target.value })}
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 14 }}>Site Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.siteContactPhone}
                    onChange={e => setFormData({ ...formData, siteContactPhone: e.target.value })}
                    style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
                  />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={formData.h2sPresent}
                      onChange={e => setFormData({ ...formData, h2sPresent: e.target.checked })}
                    />
                    <span style={{ fontWeight: 500 }}>☠️ H2S Present at this location</span>
                  </label>
                </div>
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 24, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  style={{ padding: "10px 20px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: "10px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}
                >
                  Create Wellsite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
